'use client'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { toneStyle, toneWord } from '@/lib/tone'
import type { DrSource, DrTone } from '@/lib/dr-types'
import { SourceHint } from '@/components/shell/SourceHint'

export interface StatTileProps {
  label: string
  /** The figure. Null or undefined renders nothing, never a zero (plans/00 5.8). */
  value: string | number | null | undefined
  /** Formatted figure; defaults to `value`. */
  display?: string
  sub?: string | null
  tone?: DrTone | null
  href?: string | null
  source?: DrSource | null
  className?: string
}

/** One figure: kicker, value, one sub-line, a 3px top border by tone, the source on hover. */
export function StatTile({ label, value, display, sub, tone, href, source, className }: StatTileProps) {
  if (value === null || value === undefined) return null
  const style = toneStyle(tone)
  const needsYou = tone === 'needs' || tone === 'bad'
  const body = (
    <div
      className={cn(
        'flex h-full flex-col gap-1 rounded-2xl border border-t-[3px] border-hairline bg-card p-4 shadow-soft',
        style.accent,
        needsYou && style.wash,
        className
      )}
    >
      <span className='text-xs font-semibold uppercase tracking-widest text-muted-foreground'>
        {label}
        {toneWord(tone) && <span className='sr-only'>, {toneWord(tone)}</span>}
      </span>
      <SourceHint source={source} focusable={!href}>
        <span className={cn('font-display text-3xl font-semibold leading-none', needsYou ? style.text : 'text-foreground')}>
          {display ?? String(value)}
        </span>
      </SourceHint>
      {sub && <span className='text-sm text-muted-foreground'>{sub}</span>}
    </div>
  )
  if (!href) return body
  return (
    <Link
      href={href}
      className='block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
    >
      {body}
    </Link>
  )
}
